import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, Bot, Terminal, Database, Workflow,
  MessagesSquare, BarChart3, Cloud, Brain, ArrowUpRight,
} from 'lucide-react';
import { TRAINING_PROGRAMS } from '../../data/content';

const ICONS = { Sparkles, Bot, Terminal, Database, Workflow, MessagesSquare, BarChart3, Cloud, Brain };

export default function TrainingPrograms() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="training" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal mb-4">
              — What I Train —
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter">
              Training Programs
            </h2>
            <p className="mt-4 text-white/60 max-w-xl">
              Customized for your team's role, domain, and AI maturity level.
            </p>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
            09 Programs · Fully Modular
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TRAINING_PROGRAMS.map((p, i) => {
            const Icon = ICONS[p.icon] || Sparkles;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: 0.04 * i, duration: 0.5 }}
                data-testid={`training-card-${p.id}`}
                id={`program-card-${i}`}
                className="comic-panel glass rounded-2xl p-7 flex flex-col border-2"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-xl border border-white/10 flex items-center justify-center bg-white/[0.02]">
                    <Icon size={20} className="text-teal" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="font-display font-bold text-xl text-white mb-3">
                  {p.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">
                  {p.desc}
                </p>

                <div className="flex flex-wrap gap-2 mb-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-teal bg-teal/10 border border-teal/20 px-2.5 py-1 rounded-full">
                    {p.audience}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50 border border-white/10 px-2.5 py-1 rounded-full">
                    {p.duration}
                  </span>
                </div>

                <button
                  onClick={() => scrollTo('#contact')}
                  className="group mt-auto inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.15em] text-white hover:text-teal transition-colors"
                >
                  Get Curriculum
                  <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col md:flex-row items-center justify-between gap-6 glass rounded-2xl p-8">
          <p className="font-display text-xl md:text-2xl max-w-xl">
            Don't see what you need? I design <span className="text-teal">custom training</span> for your team.
          </p>
          <button
            onClick={() => scrollTo('#contact')}
            data-testid="training-cta-custom"
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal text-[#0a0a0f] font-mono text-xs uppercase tracking-[0.15em] font-bold rounded-full hover:bg-white transition-colors"
          >
            Let's Design Your Program
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
